import { notify, SkeletonPlaceholder } from '@yembo/yemblocks-core';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ErrorWithResponse, parseErrorMessage } from 'src/api/_util/parseErrorMessage';
import { getTextThreadApi, postTextMessageApi, putTextThreadApi } from 'src/api/yembo/activityFeed';
import { TextMessage, TextMessageSenderType, TextThread } from 'src/types';
import { sortPinnedActivities } from 'src/util/activity/sortPinnedActivities';
import { TextMessageContext } from '../_contexts/TextMessageContext';
import { getReferrerCompanyName } from '../_routes/Job/Overview/ActivityFeed/ActivityRow/_util/getReferrerCompanyName';
import { AuthUserContext } from '../_shared/Context/AuthUserContext';
import { PreventUpdatingContext } from '../_shared/Context/PreventUpdatingContext/context';
import { usePrevious } from '../_shared/Hooks/usePrevious';
import { useImageSelector } from './_hooks/useImageSelector';
import { ImageToUpload, uploadImages } from './_utils/uploadImages';
import { MessageItem } from './MessageItem';
import { ReplyBox } from './ReplyBox';

export type SelectedImage = {
  key: string;
  file?: File;
  importedUrl?: string;
};

type TextMessagesProps = {
  isViewOnly?: boolean;
  existingTextThread?: TextThread | null;
  sendMessageCallback?: () => void;
  markUnreadOnOpen?: boolean;
};

export const TextMessages = ({
  existingTextThread = null,
  isViewOnly = false,
  sendMessageCallback,
  markUnreadOnOpen = true,
}: TextMessagesProps): React.JSX.Element => {
  const { authUser, activeRole } = useContext(AuthUserContext);
  const { job, rooms, videos, images, jobCompany, insured } = useContext(TextMessageContext);
  const activities = sortPinnedActivities([]);
  const allActivities = sortPinnedActivities(activities);

  const { preventUpdate } = useContext(PreventUpdatingContext);

  const [textThread, setTextThread] = useState(existingTextThread);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const { imageSelectorModal, openImageSelectorModal, selectedImages, clearImages } = useImageSelector(
    rooms,
    videos,
    images
  );

  const jobKey = job.key;

  const { textMessages, employee, key, isCopiedFromOriginalJob = false } = textThread || {};
  const prevTextMessages = usePrevious(textMessages);
  const hasNewMessage = prevTextMessages && textMessages && prevTextMessages.length !== textMessages.length;
  const hasUnreadMessage = textMessages?.some(({ unread }) => unread);
  const isSuperAdmin = activeRole === 'superadmin';

  const getInitialThread = useCallback(async () => {
    const data = { jobKey, expandedFields: ['textMessage', 'employee'] };

    const res = (await getTextThreadApi({
      data,
      onError: () => null, // don't show UI alert if error. If there's no content in the response, that means there's no existing text thread
    })) as {
      textThread: TextThread;
    };

    setTextThread(res?.textThread ?? {});
    setIsLoadingMessages(false);
  }, [jobKey]);

  const getLatestThread = useCallback(
    async (textThreadKey?: string) => {
      if (!textThreadKey) {
        return;
      }

      const data = {
        jobKey,
        key: textThreadKey,
        expandedFields: ['textMessage', 'employee'],
      } as const;

      const res = (await getTextThreadApi({ data })) as { textThread: TextThread } | undefined;
      const updatedTextTread = res?.textThread;

      if (!updatedTextTread) return;

      setTextThread((prev) => {
        const prevCount = prev?.textMessages?.length ?? 0;

        return updatedTextTread.textMessages.length !== prevCount ? updatedTextTread : prev;
      });
    },
    [jobKey]
  );

  useEffect(() => {
    if (!key) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      getInitialThread();
    } else {
      setIsLoadingMessages(false);
    }

    const intervalHandle = setInterval(() => getLatestThread(key), 5000); // poll for new incoming messages
    return () => {
      clearInterval(intervalHandle);
    };
  }, [getLatestThread, getInitialThread, key]);

  useEffect(() => {
    const handleUnread = () => {
      if (hasUnreadMessage && !isSuperAdmin && markUnreadOnOpen) {
        const data = { key, unread: false };
        const markUnread = async () => (await putTextThreadApi({ data })) as Promise<void>;
        preventUpdate(markUnread);
      }
    };

    handleUnread();
  }, [hasUnreadMessage, key, markUnreadOnOpen, isSuperAdmin, preventUpdate]);

  const scrollToBottom = useCallback(
    (ref: HTMLDivElement | null) => {
      if (!ref) return;
      if (!isLoadingMessages || hasNewMessage) {
        ref.scrollTop = ref.scrollHeight;
      }
    },
    [isLoadingMessages, hasNewMessage]
  );

  const getName = (senderType: TextMessageSenderType) => {
    if (senderType === 'insured') {
      return insured.givenName;
    }

    if (isCopiedFromOriginalJob) {
      return getReferrerCompanyName(allActivities, jobKey);
    }

    if (!jobCompany.isCrmEnabled) {
      return jobCompany.name;
    }

    return employee?.givenName ?? jobCompany.name;
  };

  const handleSend = async (message: string, clearMessage: () => void) => {
    if (isSending) return;

    setIsSending(true);

    const imagesToUpload = selectedImages.filter(({ file }) => file) as ImageToUpload[];
    const uploadedImages = await uploadImages(imagesToUpload, job.companyKey);
    const imageUrls = selectedImages.map(({ key, importedUrl }) => {
      const uploadedImage = uploadedImages.find((image) => image.key === key);
      if (uploadedImage) {
        return uploadedImage.url;
      }

      return importedUrl?.split('?X-Amz-Content')[0];
    });

    const data = {
      employeeKey: authUser.key,
      jobKey,
      companyKey: job.companyKey,
      message,
      imageUrls,
      textThreadKey: key,
      send: true,
    };

    const textMessageResult = (await postTextMessageApi({
      data,
      onError: (e: unknown) => {
        notify.error(parseErrorMessage(e as ErrorWithResponse));
        setIsSending(false);
      },
    })) as { textMessage: TextMessage };

    if (textMessageResult?.textMessage) {
      await getLatestThread(textMessageResult?.textMessage.textThreadKey); // need to give it the textThread key in case it's the first message on a new textThread

      if (textThread?.unread) {
        // mark as unread if sending from the inbox
        await putTextThreadApi({ data: { key, unread: false } });
      }

      clearImages();
      clearMessage();
      sendMessageCallback?.();
    }
    setIsSending(false);
  };

  const renderReplyAbility = () => {
    if (isViewOnly || isLoadingMessages) {
      return null;
    }

    if (isCopiedFromOriginalJob) {
      return <div className='add-phone-warning'>Communication disabled on text threads from a referring company.</div>;
    }

    if (!insured.phone) {
      return <div className='add-phone-warning'>Add a customer phone number to send a message.</div>;
    }

    if (!jobCompany.chatEnabled) {
      return <div className='add-phone-warning'>Chat is not enabled for this company.</div>;
    }

    return (
      <ReplyBox
        isSending={isSending}
        selectedImages={selectedImages}
        onOpenImageSelectorModal={openImageSelectorModal}
        onSubmit={(message, clearMessage) => preventUpdate(() => handleSend(message, clearMessage))}
      />
    );
  };

  const loadingClassName = isLoadingMessages ? 'loading' : '';
  const sendingClassName = isSending ? 'sending' : '';

  return (
    <div className={`text-messages ${sendingClassName} ${loadingClassName}`}>
      <div className='messages-wrap' ref={scrollToBottom}>
        {isLoadingMessages ? (
          <div className='messages-skeleton'>
            {Array.from({ length: 4 }).map((_, i) => (
              <div className={`message-skeleton ${i % 2 === 0 ? 'left' : 'right'}`} key={`msg-skel-${i}`}>
                <SkeletonPlaceholder />
              </div>
            ))}
          </div>
        ) : (
          textMessages?.map((message) => (
            <div className='message-wrap' key={message.key}>
              <div className={`message ${message.senderType === 'insured' ? 'insured' : 'employee'}`}>
                <div className='name-time-wrap'>
                  <div className='bold'>{getName(message.senderType)}</div>
                  <div className='date caption'>{dayjs(message.createdAt).fromNow()}</div>
                </div>
                <div>
                  <MessageItem
                    message={message}
                    insuredName={`${insured.givenName} ${insured.familyName}`}
                    isCopiedFromOriginalJob={isCopiedFromOriginalJob}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {renderReplyAbility()}
      {imageSelectorModal}
    </div>
  );
};
